// Locale-aware helpers driven by browser Intl API.
// Currency is inferred from navigator.language when possible; fallback USD.

const LOCALE_CURRENCY_MAP = {
  US: "USD", GB: "GBP", AU: "AUD", NZ: "NZD", CA: "CAD",
  IE: "EUR", DE: "EUR", FR: "EUR", ES: "EUR", IT: "EUR", NL: "EUR", PT: "EUR", AT: "EUR",
  JP: "JPY", KR: "KRW", CN: "CNY", HK: "HKD", SG: "SGD", IN: "INR", BR: "BRL", MX: "MXN",
  ZA: "ZAR", CH: "CHF", SE: "SEK", NO: "NOK", DK: "DKK", PH: "PHP", TH: "THB", ID: "IDR",
  MY: "MYR", AE: "AED", SA: "SAR",
};

// Map IANA timezone → ISO country. Enough coverage for common locations.
const TIMEZONE_COUNTRY_MAP = {
  "Australia/Sydney": "AU", "Australia/Melbourne": "AU", "Australia/Brisbane": "AU",
  "Australia/Perth": "AU", "Australia/Adelaide": "AU", "Australia/Hobart": "AU",
  "Australia/Darwin": "AU",
  "Pacific/Auckland": "NZ",
  "Asia/Tokyo": "JP", "Asia/Seoul": "KR", "Asia/Shanghai": "CN", "Asia/Hong_Kong": "HK",
  "Asia/Singapore": "SG", "Asia/Kolkata": "IN", "Asia/Calcutta": "IN", "Asia/Bangkok": "TH",
  "Asia/Jakarta": "ID", "Asia/Kuala_Lumpur": "MY", "Asia/Manila": "PH", "Asia/Dubai": "AE",
  "Asia/Riyadh": "SA",
  "Europe/London": "GB", "Europe/Dublin": "IE",
  "Europe/Paris": "FR", "Europe/Berlin": "DE", "Europe/Madrid": "ES", "Europe/Rome": "IT",
  "Europe/Amsterdam": "NL", "Europe/Lisbon": "PT", "Europe/Vienna": "AT",
  "Europe/Zurich": "CH", "Europe/Stockholm": "SE", "Europe/Oslo": "NO", "Europe/Copenhagen": "DK",
  "America/New_York": "US", "America/Chicago": "US", "America/Denver": "US", "America/Los_Angeles": "US",
  "America/Phoenix": "US", "America/Anchorage": "US", "Pacific/Honolulu": "US",
  "America/Toronto": "CA", "America/Vancouver": "CA", "America/Edmonton": "CA",
  "America/Mexico_City": "MX", "America/Sao_Paulo": "BR",
  "Africa/Johannesburg": "ZA",
};

function getTimezoneCountry() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TIMEZONE_COUNTRY_MAP[tz] || null;
  } catch { return null; }
}

export function getLocale() {
  const raw = typeof navigator !== "undefined" ? navigator.language || "en-US" : "en-US";
  // Strip POSIX (@posix) or other extensions that Intl doesn't accept
  const cleaned = raw.split("@")[0].replace(/_/g, "-");
  try {
    // Validate: throws if invalid
    new Intl.Locale(cleaned);
    return cleaned;
  } catch {
    return "en-US";
  }
}

export function getCurrency() {
  // Timezone gives the most reliable "physical location" signal, so try it first.
  const tzCountry = getTimezoneCountry();
  if (tzCountry && LOCALE_CURRENCY_MAP[tzCountry]) return LOCALE_CURRENCY_MAP[tzCountry];
  const locale = getLocale();
  const region = locale.split("-")[1]?.toUpperCase();
  return (region && LOCALE_CURRENCY_MAP[region]) || "USD";
}

// Exposed so the UI can offer a "detect my currency" affordance.
export function detectCurrencyFromLocation() {
  const tzCountry = getTimezoneCountry();
  if (tzCountry && LOCALE_CURRENCY_MAP[tzCountry]) return LOCALE_CURRENCY_MAP[tzCountry];
  const region = getLocale().split("-")[1]?.toUpperCase();
  return (region && LOCALE_CURRENCY_MAP[region]) || "USD";
}

export function formatMoney(amount, opts = {}) {
  if (amount == null || Number.isNaN(Number(amount))) return "-";
  return new Intl.NumberFormat(getLocale(), {
    style: "currency",
    currency: opts.currency || getCurrency(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount));
}

// Return the display symbol for an ISO 4217 code (e.g. "AUD" -> "A$",
// "USD" -> "$", "EUR" -> "€", "GBP" -> "£"). Falls back to the ISO code
// itself when the platform's Intl data cannot resolve one. Used by every
// export (PDF / XLSX / CSV) so nothing hardcodes a "$".
export function getCurrencySymbol(currencyCode, locale) {
  const code = currencyCode || getCurrency();
  const loc = locale || getLocale();
  try {
    const parts = new Intl.NumberFormat(loc, {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol",
    }).formatToParts(0);
    const sym = parts.find((p) => p.type === "currency");
    if (sym && sym.value) return sym.value;
  } catch {
    // fall through
  }
  try {
    const parts = new Intl.NumberFormat(loc, {
      style: "currency",
      currency: code,
    }).formatToParts(0);
    const sym = parts.find((p) => p.type === "currency");
    if (sym && sym.value) return sym.value;
  } catch {
    // ignore
  }
  return code;
}

export function formatPercent(value, digits = 0) {
  if (value == null || Number.isNaN(Number(value))) return "-";
  return new Intl.NumberFormat(getLocale(), {
    style: "percent",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number(value) / 100);
}

export function formatDate(dateInput) {
  if (!dateInput) return "-";
  const d = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  return new Intl.DateTimeFormat(getLocale(), {
    dateStyle: "medium",
  }).format(d);
}

export function formatDateTime(dateInput) {
  if (!dateInput) return "-";
  const d = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  return new Intl.DateTimeFormat(getLocale(), {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
}

export function formatQty(qty, unit) {
  const n = Number(qty);
  if (Number.isNaN(n)) return `: ${unit || ""}`;
  const formatted = new Intl.NumberFormat(getLocale(), {
    maximumFractionDigits: 2,
  }).format(n);
  return `${formatted} ${unit || ""}`.trim();
}
